import * as maptilersdk from "@maptiler/leaflet-maptilersdk"
import gsap from "gsap"
import L from "leaflet"
import "leaflet/dist/leaflet.css"
import { perfTime } from "../utils/perf"
import { prefetchUrl } from "../utils/prefetch"

class Map {
  constructor() {
    this.markers = []
    this.primoMarkers = []
    this.activeSingleSlug = null

    this.el = document.querySelector("[data-map]")
    this.controlCorners = document.querySelectorAll("[data-map-controls-corner]")

    this.initializeMap()
    this.addTileLayer()
    this.createElements()
    this.createPrimoElements()
    this.bindTooltipPositioning()
    this.checkTemplate()

    window.H.on("NAVIGATE_IN", () => {
      const endNav = perfTime("Map NAVIGATE_IN handler")

      this.hideAllMarkersSync()

      setTimeout(() => {
        const endCheck = perfTime("Map checkTemplate (deferred)")
        this.checkTemplate()
        endCheck()

        const endOn = perfTime("Map on() rebind")
        this.on()
        endOn()

        endNav()
      }, 0)
    })
  }

  hideAllMarkersSync() {
    const end = perfTime("hideAllMarkersSync")
    let i = this.markers.length

    while (i--) {
      const item = this.markers[i].item
      gsap.killTweensOf(item)
      gsap.set(item, { opacity: 0, autoAlpha: 0 })
    }

    i = this.primoMarkers.length
    while (i--) {
      const item = this.primoMarkers[i].item
      gsap.killTweensOf(item)
      gsap.set(item, { opacity: 0, autoAlpha: 0 })
    }

    this.activeSingleSlug = null
    end({ markers: this.markers.length, primos: this.primoMarkers.length })
  }

  setMarkerHidden(item, animate = true) {
    gsap.killTweensOf(item)
    const values = { opacity: 0, autoAlpha: 0 }

    if (animate) {
      gsap.to(item, { duration: 0.5, ...values })
    } else {
      gsap.set(item, values)
    }
  }

  setMarkerVisible(item, animate = true) {
    gsap.killTweensOf(item)
    const values = { opacity: 1, autoAlpha: 1 }

    if (animate) {
      gsap.to(item, { duration: 0.5, ...values })
    } else {
      gsap.set(item, values)
    }
  }

  initializeMap() {
    this.map = L.map("map", {
      zoomControl: false,
    }).setView([50.8503, 4.3517], 13.5)

    this.on()
  }

  toggleFullscreen() {
    if (!document.fullscreenElement) {
      if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen()
      } else if (document.documentElement.mozRequestFullScreen) {
        document.documentElement.mozRequestFullScreen()
      } else if (document.documentElement.webkitRequestFullscreen) {
        document.documentElement.webkitRequestFullscreen()
      } else if (document.documentElement.msRequestFullscreen) {
        document.documentElement.msRequestFullscreen()
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen()
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen()
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen()
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen()
      }
    }
  }

  on() {
    window.addEventListener("map:zoomIn", () => {
      this.map.zoomIn()
    })
    window.addEventListener("map:zoomOut", () => {
      this.map.zoomOut()
    })
    window.addEventListener("map:fullscreen", this.toggleFullscreen.bind(this))
    window.addEventListener("map:selection", this.onSelectionUpdate.bind(this))
    window.addEventListener(
      "map:selection-primo",
      this.onPrimoSelectionUpdate.bind(this)
    )
  }

  onPrimoSelectionUpdate() {
    const end = perfTime("onPrimoSelectionUpdate")
    const { primo } = window._selection

    if (!primo) {
      this.showPrimoElements()
      end({ mode: "all-primos" })
      return
    }

    let i = this.primoMarkers.length
    while (i--) {
      const marker = this.primoMarkers[i]
      const visible = marker.categories.includes(primo)
      if (visible) {
        this.setMarkerVisible(marker.item)
      } else {
        this.setMarkerHidden(marker.item)
      }
    }

    end({ primo, total: this.primoMarkers.length })
  }

  onSelectionUpdate() {
    const end = perfTime("onSelectionUpdate")
    const { chronologies, typology, communes, communautes } = window._selection

    if (
      !chronologies.length &&
      !typology &&
      !communes.length &&
      !communautes.length
    ) {
      this.showElements()
      end({ mode: "all-elements" })
      return
    }

    let i = this.markers.length
    while (i--) {
      const marker = this.markers[i]
      const typologyMatch = typology
        ? marker.typologies.includes(typology)
        : false
      const chronologiesMatch = marker.chronologies.some((chronology) =>
        chronologies.includes(chronology)
      )
      const communesMatch = marker.communes.some((commune) =>
        communes.includes(commune)
      )
      const communautesMatch = marker.communautes.some((communaute) =>
        communautes.includes(communaute)
      )
      const visible =
        chronologiesMatch ||
        typologyMatch ||
        communesMatch ||
        communautesMatch

      if (visible) {
        this.setMarkerVisible(marker.item)
      } else {
        this.setMarkerHidden(marker.item)
      }
    }

    end({ total: this.markers.length })
  }

  addTileLayer() {
    const mtLayer = maptilersdk
      .maptilerLayer({
        apiKey: "KGSmWZT62hQ8GPknQ7to",
        style: "b34e9974-c928-4f80-b25a-78134b6166fe",
      })
      .addTo(this.map)
  }

  getMarkerPopupHtml({ title, address, thumbnail = null }) {
    const thumbnailContent = thumbnail
      ? `<img src="${thumbnail}" alt="" />`
      : `<div data-marker-placeholder></div>`

    return `
    <div data-marker-title>
      <div data-marker-media>
        ${thumbnailContent}
      </div>
      <div data-marker-body>
        <span class="font-semibold">${title}</span><br>
        <span>${address}</span>
      </div>
    </div>`
  }

  getMarkerLabelHtml({ title, address }) {
    return `
    <div data-marker-title data-marker-title-label>
      <span class="font-semibold">${title}</span><br>
      <span>${address}</span>
    </div>`
  }

  updateTooltipPosition(wrapper) {
    const tooltip = wrapper.querySelector("[data-marker-title]")
    if (!tooltip) return

    tooltip.classList.remove("is-above")

    const previousVisibility = tooltip.style.visibility
    tooltip.style.visibility = "hidden"
    tooltip.style.display = "block"

    const markerRect = wrapper.getBoundingClientRect()
    const tooltipRect = tooltip.getBoundingClientRect()
    const gap = 8
    const controlCorners = document.querySelectorAll("[data-map-controls-corner]")
    const bottomLimit = controlCorners.length
      ? Math.min(
          ...Array.from(controlCorners).map((el) => el.getBoundingClientRect().top)
        )
      : window.innerHeight

    if (markerRect.bottom + gap + tooltipRect.height > bottomLimit) {
      tooltip.classList.add("is-above")
    }

    tooltip.style.visibility = previousVisibility
    tooltip.style.removeProperty("display")
  }

  updateVisibleTooltipPositions() {
    const end = perfTime("updateVisibleTooltipPositions")
    const isSingleElement = document.body.classList.contains("single-element")
    let updated = 0

    if (isSingleElement && this.activeSingleSlug) {
      let i = this.markers.length
      while (i--) {
        const marker = this.markers[i]
        if (marker.slug !== this.activeSingleSlug) continue

        const wrapper = marker.item?.querySelector(".custom-marker-wrapper")
        if (wrapper) {
          this.updateTooltipPosition(wrapper)
          updated++
        }
        break
      }

      end({ mode: "single-element", updated })
      return
    }

    let i = this.markers.length
    while (i--) {
      const wrapper = this.markers[i].item?.querySelector(".custom-marker-wrapper")
      if (!wrapper) continue

      const tooltip = wrapper.querySelector("[data-marker-title]")
      if (!tooltip) continue

      const isHovered = wrapper.matches(":hover")
      if (isHovered) {
        this.updateTooltipPosition(wrapper)
        updated++
      }
    }

    i = this.primoMarkers.length
    while (i--) {
      const wrapper = this.primoMarkers[i].item?.querySelector(
        ".custom-marker-wrapper"
      )
      if (!wrapper) continue

      const tooltip = wrapper.querySelector("[data-marker-title]")
      if (!tooltip) continue

      if (wrapper.matches(":hover")) {
        this.updateTooltipPosition(wrapper)
        updated++
      }
    }

    end({ mode: "hover", updated })
  }

  getElementUrl(slug) {
    return `${window.location.origin}/element/${slug}`
  }

  prefetchMarkerPage(slug) {
    if (document.body.classList.contains("single-element")) return
    prefetchUrl(this.getElementUrl(slug))
  }

  bindTooltipPositioning() {
    this.updateVisibleTooltipPositions =
      this.updateVisibleTooltipPositions.bind(this)

    let i = this.markers.length
    while (i--) {
      const marker = this.markers[i]
      const wrapper = marker.item?.querySelector(".custom-marker-wrapper")
      if (!wrapper) continue

      wrapper.addEventListener("mouseenter", () => {
        this.updateTooltipPosition(wrapper)
        this.prefetchMarkerPage(marker.slug)
      })
    }

    i = this.primoMarkers.length
    while (i--) {
      const wrapper = this.primoMarkers[i].item?.querySelector(
        ".custom-marker-wrapper"
      )
      if (!wrapper) continue

      wrapper.addEventListener("mouseenter", () => {
        this.updateTooltipPosition(wrapper)
      })
    }

    this.map.on("moveend zoomend", this.updateVisibleTooltipPositions)
    window.addEventListener("resize", this.updateVisibleTooltipPositions)

    if (document.body.classList.contains("single-element")) {
      requestAnimationFrame(this.updateVisibleTooltipPositions)
    }
  }

  createElements() {
    const end = perfTime("createElements")
    const { elements } = window.appData
    const isSingleElement = document.body.classList.contains("single-element")
    const singleSlug = isSingleElement
      ? document.querySelector("[data-item]")?.dataset?.slug
      : null
    let i = 0
    const len = elements.length

    for (; i < len; i++) {
      const element = elements[i]
      const {
        lat,
        lng,
        typologie,
        chronologie,
        commune,
        communaute,
        archived,
        title,
        address,
        thumbnail,
        slug,
      } = element
      if (!lat || !lng) continue

      const customIconHtml = `
  <div class="custom-marker-wrapper relative ${archived ? "archived" : ""}">
    <img src="${
      window.appData.themeUri
    }/public/images/coeur.svg" width="40" height="40" alt="Marker Icon">
    ${this.getMarkerPopupHtml({ title, address, thumbnail })}
  </div>
`
      const customIcon = L.divIcon({
        html: customIconHtml,
        iconSize: [30, 30],
        className: "custom-marker",
      })

      const marker = L.marker([+lat, +lng], { icon: customIcon }).addTo(
        this.map
      )

      const item = marker.getElement()
      const typology = typologie[0]
      const typologyColor = typology
        ? window.appData.typologies[typology]["bg_color"]
        : "black"
      item.style.setProperty("--typology-color", typologyColor)

      const visibleOnLoad = isSingleElement && slug === singleSlug
      gsap.set(item, { opacity: visibleOnLoad ? 1 : 0, autoAlpha: visibleOnLoad ? 1 : 0 })

      if (visibleOnLoad) {
        this.activeSingleSlug = slug
      }

      this.markers.push({
        slug,
        item,
        marker,
        typologies: typologie,
        communes: commune,
        communautes: communaute,
        chronologies: chronologie,
      })

      marker.on("click", () => {
        if (document.body.classList.contains("single-element")) return
        window.H.redirect(this.getElementUrl(slug))
      })
    }

    end({ total: elements.length, created: this.markers.length, isSingleElement })
  }

  createPrimoElements() {
    const end = perfTime("createPrimoElements")
    const { primos } = window.appData
    let i = 0
    const len = primos.length

    for (; i < len; i++) {
      const element = primos[i]
      const { lat, lng, categories, title, address } = element
      if (!lat || !lng) continue

      const customIconHtml = `
  <div class="relative custom-marker-wrapper">
    <img src="${
      window.appData.themeUri
    }/public/images/coeur.svg" width="40" height="40" alt="Marker Icon">
    ${this.getMarkerLabelHtml({ title, address })}
  </div>
`

      const customIcon = L.divIcon({
        html: customIconHtml,
        iconSize: [30, 30],
        className: "custom-marker",
      })

      const marker = L.marker([+lat, +lng], { icon: customIcon }).addTo(
        this.map
      )

      const item = marker.getElement()
      item.style.setProperty("--typology-color", "red")

      gsap.set(item, { opacity: 0, autoAlpha: 0 })
      this.primoMarkers.push({
        slug: element.slug,
        item,
        marker,
        categories,
      })
    }

    end({ total: primos.length, created: this.primoMarkers.length })
  }

  showFilterElement(typology) {
    const end = perfTime("showFilterElement")
    let i = this.markers.length

    while (i--) {
      const marker = this.markers[i]
      const visible = !typology || marker.typologies.includes(typology)
      if (visible) {
        this.setMarkerVisible(marker.item)
      } else {
        this.setMarkerHidden(marker.item)
      }
    }

    if (!typology) {
      this.map.setView([50.8503, 4.3517], 13)
    }

    end({ typology, total: this.markers.length })
  }

  showMap() {
    gsap.to(this.el, {
      duration: 0.5,
      opacity: 1,
    })
  }

  showControls() {
    this.controlCorners.forEach((corner) => {
      corner.style.removeProperty("pointer-events")
      corner.classList.remove("hidden")
      gsap.to(corner, {
        duration: 0.5,
        opacity: 1,
      })
    })
  }

  hideMap() {
    // gsap.set(this.el, { autoAlpha: 0, display: 'block' })
    gsap.to(this.el, {
      duration: 0.5,
      opacity: 0,
    })
  }

  hideControls() {
    this.controlCorners.forEach((corner) => {
      corner.style.pointerEvents = "none"
      corner.classList.add("hidden")
      gsap.to(corner, {
        duration: 0.5,
        opacity: 0,
      })
    })
  }

  showElement(slug) {
    const end = perfTime("showElement")
    this.activeSingleSlug = slug
    let activeWrapper = null
    let i = this.markers.length

    while (i--) {
      const marker = this.markers[i]
      if (marker.slug === slug) {
        gsap.killTweensOf(marker.item)
        gsap.to(marker.item, {
          duration: 0.5,
          opacity: 1,
          autoAlpha: 1,
          onComplete: () => {
            const wrapper = marker.item?.querySelector(".custom-marker-wrapper")
            if (wrapper) this.updateTooltipPosition(wrapper)
          },
        })
        activeWrapper = marker.item?.querySelector(".custom-marker-wrapper")
      } else {
        this.setMarkerHidden(marker.item, false)
      }
    }

    if (activeWrapper) {
      this.updateTooltipPosition(activeWrapper)
    }

    end({ slug, total: this.markers.length })
  }

  showElements() {
    const end = perfTime("showElements")
    this.activeSingleSlug = null
    let i = this.markers.length

    while (i--) {
      this.setMarkerVisible(this.markers[i].item)
    }

    i = this.primoMarkers.length
    while (i--) {
      this.setMarkerHidden(this.primoMarkers[i].item)
    }

    end({ markers: this.markers.length, primos: this.primoMarkers.length })
  }

  showPrimoElements() {
    const end = perfTime("showPrimoElements")
    this.activeSingleSlug = null
    let i = this.markers.length

    while (i--) {
      this.setMarkerHidden(this.markers[i].item)
    }

    i = this.primoMarkers.length
    while (i--) {
      this.setMarkerVisible(this.primoMarkers[i].item)
    }

    end({ markers: this.markers.length, primos: this.primoMarkers.length })
  }

  checkTemplate() {
    const end = perfTime("checkTemplate")
    const bodyClasses = document.body.classList

    requestAnimationFrame(() => {
      const isElementView = bodyClasses.contains("single-element")
      const isElementsView = bodyClasses.contains("template-carte")
      const isPrimoView = bodyClasses.contains("template-primo")

      if (isElementView) {
        const endShow = perfTime("checkTemplate.showElement")
        const item = document.querySelector("[data-item]")
        if (item) {
          this.showElement(item.dataset.slug)
        }
        endShow({ slug: item?.dataset?.slug })

        requestAnimationFrame(() => {
          const endTooltip = perfTime("checkTemplate.updateTooltips")
          this.updateVisibleTooltipPositions()
          endTooltip()
        })
      } else if (isElementsView) {
        const endShow = perfTime("checkTemplate.showElements")
        this.showElements()
        endShow()
      } else if (isPrimoView) {
        const endShow = perfTime("checkTemplate.showPrimoElements")
        this.showPrimoElements()
        endShow()
      }

      const endMap = perfTime("checkTemplate.mapVisibility")
      if (isElementsView || isElementView || isPrimoView) {
        this.showMap()
      } else {
        this.hideMap()
      }
      endMap({
        visible: isElementsView || isElementView || isPrimoView,
      })

      end({
        isElementView,
        isElementsView,
        isPrimoView,
      })
    })
  }
}

export default Map
